import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoAwesome as SparkleIcon } from '../ui/icons';
import AssistantPanel from './AssistantPanel';
import { useAssistantChat } from './useAssistantChat';

interface AssistantLauncherProps {
  /**
   * `navbar` — round icon button for the mobile top bar (matches the bell there).
   * `sidebar` — full-width row for the desktop sidebar / mobile drawer (matches
   * the SidebarActions buttons).
   */
  variant?: 'navbar' | 'sidebar';
  /** Sidebar collapse state — only used by the `sidebar` variant. */
  collapsed?: boolean;
}

/**
 * App-wide launcher for the "Senior špeditér" AI assistant. Self-contained
 * (button + chat panel + conversation state), mirroring how NotificationPanel is
 * instantiated next to the notification bell in both the sidebar and the mobile
 * navbar. Rendered only inside the authenticated app shell.
 */
const AssistantLauncher: React.FC<AssistantLauncherProps> = ({
  variant = 'navbar',
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const chat = useAssistantChat();

  const toggle = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor((prev) => (prev ? null : e.currentTarget));
  };

  return (
    <>
      {variant === 'sidebar' ? (
        <button
          onClick={toggle}
          title={collapsed ? t('assistant.title') : undefined}
          aria-label={t('assistant.open')}
          className={[
            'relative flex items-center min-h-[44px] rounded-[12px] w-full border-0 p-0 cursor-pointer bg-transparent',
            'text-muted transition-all duration-300',
            'hover:translate-x-1 hover:text-primary hover:bg-primary/10',
            collapsed ? 'justify-center px-2' : 'justify-start px-3',
          ].join(' ')}
        >
          <span
            className={[
              'flex items-center justify-center flex-shrink-0',
              'transition-[margin] duration-300 ease-in-out',
              collapsed ? 'mr-0' : 'mr-2.5',
            ].join(' ')}
          >
            <SparkleIcon size={20} />
          </span>
          <span
            title={t('assistant.title')}
            className={[
              // `truncate` (nowrap + hidden + ellipsis) + `min-w-0` so the label
              // ellipsizes cleanly instead of clipping mid-letter while the
              // sidebar width animates; opacity fades smoothly on collapse.
              'text-sm font-medium truncate text-left min-w-0',
              'transition-all duration-300 ease-in-out',
              collapsed ? 'w-0 opacity-0 -translate-x-5' : 'flex-1 opacity-100 translate-x-0',
            ].join(' ')}
          >
            {t('assistant.title')}
          </span>
        </button>
      ) : (
        <button
          onClick={toggle}
          aria-label={t('assistant.open')}
          className="relative p-2 rounded-full text-muted hover:text-primary hover:bg-primary/10 transition-colors duration-200"
        >
          <SparkleIcon size={24} />
        </button>
      )}

      <AssistantPanel anchorEl={anchor} onClose={() => setAnchor(null)} chat={chat} />
    </>
  );
};

export default AssistantLauncher;
